<script setup lang="ts">
/**
 * Sync tray (bottom corner, app-wide): each publish's players filling a bar until every one PLAYS the new content.
 * Green → fades out; a stuck player turns it amber and it stays. "View" lists every player, problems first.
 * A problem turns the header amber (⚠ + count), names the player and why, and folds the tray to that header a few
 * seconds later so it never sits over the page; it opens again on a click (and folds again only for a new problem).
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { dismissRollout, playerSync, type PlayerSync, type Rollout, rollouts, screensById, summary, SYNC_RANK } from '../stores/rollouts';
import { formatAgo } from '../utils/format';
import SlideOverDrawer from './SlideOverDrawer.vue';

const { t, te } = useI18n();
const collapsed = ref(false);
const openId = ref<string | null>(null);
const open = computed(() => rollouts.find((r) => r.id === openId.value) ?? null);

/** Players with a problem across every followed publish (header count; a rise folds the tray). */
const problems = computed(() => rollouts.reduce((n, r) => n + summary(r).problem, 0));
let foldTimer: ReturnType<typeof setTimeout> | undefined;
watch(problems, (now, before) => {
  clearTimeout(foldTimer);
  if (now > (before ?? 0)) foldTimer = setTimeout(() => (collapsed.value = true), 6_000);
}, { immediate: true });
/** The first player in trouble and why, said in the row itself (not just "1 problem"). */
function firstProblem(r: Rollout): string | null {
  const p = r.players.find((x) => ['failing', 'problem', 'partial'].includes(playerSync(x)));
  if (!p) return null;
  const c = screensById.value.get(p.screenId)?.content;
  const st = playerSync(p);
  const what =
    st === 'failing'
      ? t('sync.st_failing', { why: syncErrorText(c?.syncError?.code) })
      : st === 'partial'
        ? t('sync.st_partial', { n: c?.filesFailed ?? 0, why: why(c?.reason) }, c?.filesFailed ?? 0)
        : t('sync.st_problem', { n: c?.filesFailed ?? 0, why: why(c?.reason) }, c?.filesFailed ?? 0);
  return `${p.name}: ${what}`;
}

const tone = (r: Rollout) => {
  const s = summary(r);
  return s.complete && !s.problem ? 'done' : s.problem || s.offline ? 'warn' : 'busy';
};
const BAR = { done: 'bg-emerald-500', warn: 'bg-amber-500', busy: 'bg-indigo-500' } as const;

const ICON: Record<PlayerSync, { icon: string; cls: string }> = {
  done: { icon: '✓', cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' },
  partial: { icon: '⚠', cls: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300' }, // as the "files missing" badge
  downloading: { icon: '⤓', cls: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300' },
  waiting: { icon: '↻', cls: 'bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300' },
  failing: { icon: '!', cls: 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300' },
  offline: { icon: '⏸', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' },
  problem: { icon: '⚠', cls: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300' },
};
const rows = computed(() =>
  (open.value?.players ?? [])
    .map((p) => ({ ...p, sync: playerSync(p), s: screensById.value.get(p.screenId) }))
    .sort((a, b) => SYNC_RANK[a.sync] - SYNC_RANK[b.sync] || a.name.localeCompare(b.name)),
);
const syncErrorText = (code: string | undefined) => (code && te(`sync.err_${code}`) ? t(`sync.err_${code}`) : t('sync.err_other'));
const why = (r: string | null | undefined) => (r && te(`health.why_${r}`) ? t(`health.why_${r}`) : t('sync.whyUnknown'));
function line(row: (typeof rows.value)[number]): string {
  const c = row.s?.content;
  switch (row.sync) {
    case 'done':
      return t('sync.st_done');
    case 'partial':
      return t('sync.st_partial', { n: c?.filesFailed ?? 0, why: why(c?.reason) }, c?.filesFailed ?? 0);
    case 'downloading':
      if (row.change) return t('sync.st_syncing');
      return c?.downloadPercent !== null && c?.downloadPercent !== undefined ? t('sync.st_downloadingPct', { pct: c.downloadPercent }) : t('sync.st_downloading');
    case 'offline':
      return row.s?.lastSeenAt ? t('sync.st_offlineSince', { ago: formatAgo(row.s.lastSeenAt, Date.now()) }) : t('sync.st_offline');
    case 'problem':
      return t('sync.st_problem', { n: c?.filesFailed ?? 0, why: why(c?.reason) }, c?.filesFailed ?? 0);
    case 'failing':
      return t('sync.st_failing', { why: syncErrorText(c?.syncError?.code) });
    default:
      return t('sync.st_waiting');
  }
}
function headline(r: Rollout): string {
  const s = summary(r);
  if (s.complete && !s.problem) return t('sync.allDone', { n: s.total }, s.total);
  if (s.parked) return t('sync.parked', { n: s.offline }, s.offline);
  return t('sync.readyOf', { ready: s.ready, total: s.total });
}
/** Players included only because they play in sync with a lead that was published to (the reason they appear). */
const synced = (r: Rollout) => r.players.filter((p) => p.syncedWith);
function syncNote(r: Rollout): string | null {
  const f = synced(r);
  if (!f.length) return null;
  const leads = [...new Set(f.map((p) => p.syncedWith!))];
  return leads.length === 1 ? t('sync.inclSynced', { n: f.length, lead: leads[0] }, f.length) : t('sync.inclSyncedMany', { n: f.length }, f.length);
}
function subline(r: Rollout): string {
  const s = summary(r);
  const offlineSynced = r.players.filter((p) => p.syncedWith && playerSync(p) === 'offline').length;
  return [
    s.downloading && t(r.players[0]?.change ? 'sync.nSyncing' : 'sync.nDownloading', { n: s.downloading }),
    s.waiting && t('sync.nWaiting', { n: s.waiting }),
    s.offline && (offlineSynced === s.offline ? t('sync.nOfflineSynced', { n: s.offline }, s.offline) : t('sync.nOffline', { n: s.offline })),
    s.failing && t('sync.nFailing', { n: s.failing }, s.failing),
    s.problem - s.failing && t('sync.nProblem', { n: s.problem - s.failing }, s.problem - s.failing),
  ]
    .filter(Boolean)
    .join(' · ');
}
</script>

<template>
  <!-- Fades (and rises slightly) in with the first update and out after the last; reduced motion: no animation. -->
  <Transition
    enter-active-class="motion-safe:transition motion-safe:duration-300 motion-safe:ease-out"
    enter-from-class="opacity-0 translate-y-2"
    leave-active-class="motion-safe:transition motion-safe:duration-300 motion-safe:ease-in"
    leave-to-class="opacity-0 translate-y-2"
  >
  <section
    v-if="rollouts.length"
    class="w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
    :aria-label="t('sync.tray')"
  >
    <header
      class="flex cursor-pointer items-center gap-2 px-4 py-2.5 text-white"
      :class="problems ? 'bg-amber-600 dark:bg-amber-700' : 'bg-slate-900 dark:bg-slate-800'"
      @click="collapsed = !collapsed"
    >
      <span aria-hidden="true">{{ problems ? '⚠' : '⟳' }}</span>
      <p class="flex-1 text-sm font-medium">
        {{ t('sync.tray') }}<template v-if="problems"> · {{ t('sync.nNeedAttention', { n: problems }, problems) }}</template>
      </p>
      <button type="button" class="rounded px-1.5 text-white/80 hover:text-white" :aria-label="collapsed ? t('sync.expand') : t('sync.collapse')" :aria-expanded="!collapsed" @click.stop="collapsed = !collapsed">
        {{ collapsed ? '▴' : '▾' }}
      </button>
    </header>
    <TransitionGroup
      v-if="!collapsed"
      tag="ul"
      class="max-h-72 divide-y divide-slate-100 overflow-y-auto dark:divide-slate-800"
      aria-live="polite"
      enter-active-class="motion-safe:transition motion-safe:duration-300"
      enter-from-class="opacity-0"
      leave-active-class="motion-safe:transition motion-safe:duration-300"
      leave-to-class="opacity-0"
    >
      <li v-for="r in rollouts" :key="r.id" class="px-4 py-3">
        <div class="flex items-start gap-2">
          <p class="min-w-0 flex-1 truncate text-sm font-medium" :title="r.label">{{ r.label }}</p>
          <button type="button" class="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200" :aria-label="t('sync.dismiss')" @click="dismissRollout(r.id)">✕</button>
        </div>
        <div class="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800" role="progressbar" :aria-valuenow="summary(r).pct" aria-valuemax="100">
          <div class="h-full transition-[width] duration-700" :class="BAR[tone(r)]" :style="{ width: `${summary(r).pct}%` }" />
        </div>
        <div class="mt-1.5 flex items-center gap-2 text-xs">
          <span class="font-semibold tabular-nums">{{ summary(r).pct }} %</span>
          <span class="font-medium" :class="tone(r) === 'done' ? 'text-emerald-700 dark:text-emerald-400' : tone(r) === 'warn' ? 'text-amber-700 dark:text-amber-400' : ''">{{ headline(r) }}</span>
          <span class="min-w-0 flex-1 truncate" :class="summary(r).problem ? 'font-medium text-amber-700 dark:text-amber-400' : 'text-slate-500'">{{ subline(r) }}</span>
          <button type="button" class="shrink-0 font-medium text-indigo-600 hover:underline dark:text-indigo-400" @click="openId = r.id">{{ t('sync.view') }} ▸</button>
        </div>
        <p v-if="syncNote(r)" class="mt-1 text-xs text-slate-500" :title="t('sync.syncedWhy')">⇄ {{ syncNote(r) }}</p>
        <p v-if="firstProblem(r)" class="mt-1.5 rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800 dark:bg-amber-950/60 dark:text-amber-300" :title="firstProblem(r) ?? undefined">
          ⚠ {{ firstProblem(r) }}
        </p>
      </li>
    </TransitionGroup>
  </section>
  </Transition>

  <SlideOverDrawer :open="!!open" :title="open?.label ?? ''" :description="open ? headline(open) : undefined" size="md" @close="openId = null">
    <ul v-if="open" class="divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm dark:divide-slate-800 dark:border-slate-800">
      <li v-for="row in rows" :key="row.screenId" class="flex items-start gap-3 px-3 py-2.5">
        <span class="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold" :class="ICON[row.sync].cls" aria-hidden="true">{{ ICON[row.sync].icon }}</span>
        <span class="min-w-0 flex-1">
          <RouterLink :to="{ name: 'screen-detail', params: { screenId: row.screenId } }" class="block truncate font-medium hover:underline" @click="openId = null">{{ row.name }}</RouterLink>
          <span v-if="row.syncedWith" class="mt-0.5 block text-xs text-slate-500">⇄ {{ t('sync.playsInSync', { lead: row.syncedWith }) }}</span>
          <span class="mt-0.5 block text-xs" :class="row.sync === 'problem' ? 'text-red-700 dark:text-red-400' : row.sync === 'partial' || row.sync === 'failing' ? 'text-amber-700 dark:text-amber-400' : 'text-slate-500'">{{ line(row) }}</span>
          <span v-if="row.sync === 'downloading' && row.s?.content?.downloadPercent != null" class="mt-1 block h-1 w-40 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
            <span class="block h-full bg-indigo-500 transition-[width] duration-700" :style="{ width: `${row.s.content.downloadPercent}%` }" />
          </span>
        </span>
        <RouterLink
          v-if="row.sync === 'problem' || row.sync === 'partial' || row.sync === 'offline' || row.sync === 'failing'"
          :to="{ name: row.sync === 'offline' || row.sync === 'failing' ? 'screen-detail' : 'screen-live', params: { screenId: row.screenId } }"
          class="shrink-0 text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
          @click="openId = null"
        >
          {{ row.sync === 'offline' || row.sync === 'failing' ? t('sync.checkPlayer') : t('sync.fix') }} →
        </RouterLink>
      </li>
    </ul>
    <p class="mt-3 text-xs leading-relaxed text-slate-500">{{ t('sync.hint') }}</p>
  </SlideOverDrawer>
</template>
